import * as React from 'react';
import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { useInput } from 'react-admin';
import {
  Box,
  Paper,
  ButtonGroup,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
  Code,
  FormatQuote,
  Highlight as HighlightIcon,
  Link as LinkIcon,
  LinkOff,
  Add,
  VolumeUp,
  VideoLibrary,
  Image as ImageIcon,
  CheckBox,
} from '@mui/icons-material';
import { AudioBlock } from './TiptapExtensions/AudioBlock';
import { ImageBlock } from './TiptapExtensions/ImageBlock';
import { VideoBlock } from './TiptapExtensions/VideoBlock';
import { CheckboxBlock } from './TiptapExtensions/CheckboxBlock';
import { blocksToTiptap, tiptapToBlocks } from '../utils/blocksTransformer';

interface BlocksEditorProps {
  source: string;
  label?: string;
  fullWidth?: boolean;
  helperText?: string;
}

const MenuBar = ({ editor }: { editor: any }) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL:', previousUrl);

    if (url === null) {
      return;
    }

    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const handleInsertClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const insertAudio = () => {
    const url = window.prompt('URL аудио:');
    if (url) {
      editor.chain().focus().insertAudioBlock({ url, is_blur: true }).run();
    }
    handleClose();
  };

  const insertImage = () => {
    const url = window.prompt('URL изображения:');
    if (url) {
      editor.chain().focus().insertImageBlock({ url, is_blur: true }).run();
    }
    handleClose();
  };

  const insertVideo = () => {
    const url = window.prompt('URL видео:');
    if (url) {
      editor
        .chain()
        .focus()
        .insertVideoBlock({
          url,
          preview_url: '',
          txt: '',
          presentation: '',
          summary: '',
          timecodes: [],
          is_blur: true,
        })
        .run();
    }
    handleClose();
  };

  const insertCheckbox = () => {
    editor
      .chain()
      .focus()
      .insertCheckboxBlock({ content: '', is_blur: true })
      .run();
    handleClose();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        mb: 1,
        border: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
      }}
    >
      <ButtonGroup size="small" variant="outlined">
        <Tooltip title="Заголовок 1">
          <Button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            variant={
              editor.isActive('heading', { level: 1 })
                ? 'contained'
                : 'outlined'
            }
          >
            H1
          </Button>
        </Tooltip>
        <Tooltip title="Заголовок 2">
          <Button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            variant={
              editor.isActive('heading', { level: 2 })
                ? 'contained'
                : 'outlined'
            }
          >
            H2
          </Button>
        </Tooltip>
        <Tooltip title="Заголовок 3">
          <Button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            variant={
              editor.isActive('heading', { level: 3 })
                ? 'contained'
                : 'outlined'
            }
          >
            H3
          </Button>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup size="small" variant="outlined">
        <Tooltip title="Жирный">
          <Button
            onClick={() => editor.chain().focus().toggleBold().run()}
            variant={editor.isActive('bold') ? 'contained' : 'outlined'}
          >
            <FormatBold />
          </Button>
        </Tooltip>
        <Tooltip title="Курсив">
          <Button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            variant={editor.isActive('italic') ? 'contained' : 'outlined'}
          >
            <FormatItalic />
          </Button>
        </Tooltip>
        <Tooltip title="Зачеркнутый">
          <Button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            variant={editor.isActive('strike') ? 'contained' : 'outlined'}
          >
            <FormatStrikethrough />
          </Button>
        </Tooltip>
        <Tooltip title="Выделение">
          <Button
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            variant={editor.isActive('highlight') ? 'contained' : 'outlined'}
          >
            <HighlightIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Ссылка">
          <Button
            onClick={setLink}
            variant={editor.isActive('link') ? 'contained' : 'outlined'}
          >
            <LinkIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Удалить ссылку">
          <Button
            onClick={() => editor.chain().focus().unsetLink().run()}
            disabled={!editor.isActive('link')}
          >
            <LinkOff />
          </Button>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup size="small" variant="outlined">
        <Tooltip title="Маркированный список">
          <Button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            variant={editor.isActive('bulletList') ? 'contained' : 'outlined'}
          >
            <FormatListBulleted />
          </Button>
        </Tooltip>
        <Tooltip title="Нумерованный список">
          <Button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            variant={editor.isActive('orderedList') ? 'contained' : 'outlined'}
          >
            <FormatListNumbered />
          </Button>
        </Tooltip>
        <Tooltip title="Цитата">
          <Button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            variant={editor.isActive('blockquote') ? 'contained' : 'outlined'}
          >
            <FormatQuote />
          </Button>
        </Tooltip>
        <Tooltip title="Код">
          <Button
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            variant={editor.isActive('codeBlock') ? 'contained' : 'outlined'}
          >
            <Code />
          </Button>
        </Tooltip>
      </ButtonGroup>

      <ButtonGroup size="small" variant="outlined">
        <Tooltip title="По левому краю">
          <Button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            variant={
              editor.isActive({ textAlign: 'left' }) ? 'contained' : 'outlined'
            }
          >
            <FormatAlignLeft />
          </Button>
        </Tooltip>
        <Tooltip title="По центру">
          <Button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            variant={
              editor.isActive({ textAlign: 'center' })
                ? 'contained'
                : 'outlined'
            }
          >
            <FormatAlignCenter />
          </Button>
        </Tooltip>
        <Tooltip title="По правому краю">
          <Button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            variant={
              editor.isActive({ textAlign: 'right' }) ? 'contained' : 'outlined'
            }
          >
            <FormatAlignRight />
          </Button>
        </Tooltip>
        <Tooltip title="По ширине">
          <Button
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            variant={
              editor.isActive({ textAlign: 'justify' })
                ? 'contained'
                : 'outlined'
            }
          >
            <FormatAlignJustify />
          </Button>
        </Tooltip>
      </ButtonGroup>

      <Button
        size="small"
        variant="contained"
        startIcon={<Add />}
        onClick={handleInsertClick}
        color="primary"
      >
        Вставить блок
      </Button>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={insertAudio}>
          <ListItemIcon>
            <VolumeUp />
          </ListItemIcon>
          <ListItemText>Аудио</ListItemText>
        </MenuItem>
        <MenuItem onClick={insertImage}>
          <ListItemIcon>
            <ImageIcon />
          </ListItemIcon>
          <ListItemText>Изображение</ListItemText>
        </MenuItem>
        <MenuItem onClick={insertVideo}>
          <ListItemIcon>
            <VideoLibrary />
          </ListItemIcon>
          <ListItemText>Видео</ListItemText>
        </MenuItem>
        <MenuItem onClick={insertCheckbox}>
          <ListItemIcon>
            <CheckBox />
          </ListItemIcon>
          <ListItemText>Чекбокс</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export const BlocksEditor: React.FC<BlocksEditorProps> = ({
  source,
  label,
  fullWidth = true,
  helperText,
}) => {
  const {
    field,
    fieldState: { error },
  } = useInput({ source });

  // Флаг для предотвращения циклических обновлений
  const isUpdatingRef = React.useRef(false);
  const hasInitializedRef = React.useRef(false);

  // Преобразуем blocks в Tiptap JSON при инициализации
  const initialContent = React.useMemo(
    () => blocksToTiptap(field.value || []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      AudioBlock,
      ImageBlock,
      VideoBlock,
      CheckboxBlock,
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      // Преобразуем Tiptap JSON обратно в blocks array
      isUpdatingRef.current = true;
      const tiptapJSON = editor.getJSON();
      const blocks = tiptapToBlocks(tiptapJSON);
      field.onChange(blocks);
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 0);
    },
  });

  // Синхронизация внешних изменений с редактором (только при загрузке данных)
  useEffect(() => {
    if (!editor) return;

    // Пропускаем обновления, вызванные самим редактором
    if (isUpdatingRef.current) return;

    // Только при первой загрузке или при изменении извне
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      return;
    }

    // Обновляем только если данные пришли извне (например, из API)
    if (field.value && Array.isArray(field.value)) {
      const newContent = blocksToTiptap(field.value);
      editor.commands.setContent(newContent, false);
    }
  }, [field.value, editor]);

  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto', mb: 2 }}>
      {label && (
        <Box sx={{ mb: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
          {label}
        </Box>
      )}
      <MenuBar editor={editor} />
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: error ? 'error.main' : 'divider',
          p: 2,
          minHeight: '300px',
          '& .ProseMirror': {
            minHeight: '300px',
            outline: 'none',
            '& > * + *': {
              marginTop: '0.75em',
            },
            '& ul, & ol': {
              padding: '0 1rem',
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              lineHeight: 1.1,
            },
            '& code': {
              backgroundColor: 'rgba(97, 97, 97, 0.1)',
              color: '#616161',
              padding: '0.2em 0.4em',
              borderRadius: '4px',
              fontSize: '0.9em',
            },
            '& pre': {
              background: '#0D0D0D',
              color: '#FFF',
              fontFamily: "'JetBrainsMono', monospace",
              padding: '0.75rem 1rem',
              borderRadius: '0.5rem',
              '& code': {
                color: 'inherit',
                padding: 0,
                background: 'none',
                fontSize: '0.8rem',
              },
            },
            '& mark': {
              backgroundColor: '#FAF594',
              padding: '0.1em 0.2em',
              borderRadius: '0.25em',
            },
            '& blockquote': {
              paddingLeft: '1rem',
              borderLeft: '2px solid rgba(13, 13, 13, 0.1)',
            },
            '& a': {
              color: '#1976d2',
              textDecoration: 'underline',
              cursor: 'pointer',
              '&:hover': {
                color: '#1565c0',
              },
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Paper>
      {(helperText || error) && (
        <Box
          sx={{
            mt: 0.5,
            fontSize: '0.75rem',
            color: error ? 'error.main' : 'text.secondary',
          }}
        >
          {error ? error.message : helperText}
        </Box>
      )}
    </Box>
  );
};
